import type { JSX, ReactNode } from "react";

interface LinkifyOptions {
	linkClassName?: string;
	target?: "_blank" | "_self" | "_parent" | "_top";
	rel?: string;
}

const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
const TRAILING_PUNCTUATION = /[),.;!?]+$/;

function normalizeUrl(url: string) {
	const trimmed = url.trim();
	if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
		return trimmed;
	}

	return `https://${trimmed}`;
}

function extractTrailingPunctuation(value: string) {
	let core = value;
	let trailing = "";

	while (TRAILING_PUNCTUATION.test(core)) {
		trailing = core.slice(-1) + trailing;
		core = core.slice(0, -1);
	}

	return {
		core,
		trailing,
	};
}

export function linkifyText(
	text: string,
 	options: LinkifyOptions = {},
): ReactNode[] {
	const nodes: ReactNode[] = [];
	let lastIndex = 0;

	text.replace(URL_REGEX, (match, _url, offset) => {
		if (offset > lastIndex) {
			nodes.push(text.slice(lastIndex, offset));
		}

		const { core, trailing } = extractTrailingPunctuation(match);

		if (core) {
			nodes.push(
				<a
					key={`link-${offset}-${core}`}
					href={normalizeUrl(core)}
					className={
						options.linkClassName ?? "text-primary-600 underline break-words"
					}
					target={options.target ?? "_blank"}
					rel={options.rel ?? "noopener noreferrer"}
				>
					{core}
				</a>,
			);
		}

		if (trailing) {
			nodes.push(trailing);
		}

		lastIndex = offset + match.length;

		return match;
	});

	if (lastIndex < text.length) {
		nodes.push(text.slice(lastIndex));
	}

	return nodes;
}


interface MultilineOptions extends LinkifyOptions {
	paragraphClassName?: string;
	preserveEmptyLines?: boolean;
}

export function renderMultilineTextWithLinks(
	text: string,
	options: MultilineOptions = {},
): JSX.Element[] {
	const {
		paragraphClassName,
		preserveEmptyLines = false,
		...linkifyOptions
	} = options;

	const paragraphs = text.split(/\r?\n/);

	return paragraphs.map((paragraph, index) => {
		const content = paragraph.trim();

		if (!content && !preserveEmptyLines) {
			return (
				<p key={`paragraph-${index}`} className={paragraphClassName}>
					<br />
				</p>
			);
		}

		return (
			<p key={`paragraph-${index}`} className={paragraphClassName}>
				{linkifyText(paragraph, linkifyOptions)}
			</p>
		);
	});
}
